import axios from "axios";
import React, { createContext, useState, useEffect } from "react";
import authApi from "../api/auth";
import kitchensApi from "../api/kitchens";
import { Admin, Kitchen, LoginResponse } from "../types/auth";

interface AuthContextProps {
  isAuthenticated: boolean;
  admin: Admin | null;
  kitchens: Kitchen[];
  selectedKitchenId: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  logout: () => void;
  setSelectedKitchenId: (kitchenId: string | null) => void;
  refreshKitchens: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  admin: null,
  kitchens: [],
  selectedKitchenId: null,
  loading: true,
  login: async () => Promise.resolve({} as LoginResponse),
  logout: () => {},
  setSelectedKitchenId: () => {},
  refreshKitchens: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [selectedKitchenIdState, setSelectedKitchenIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const setSelectedKitchenId = (kitchenId: string | null) => {
    setSelectedKitchenIdState(kitchenId);
    if (kitchenId) {
      localStorage.setItem("selectedKitchenId", kitchenId);
      return;
    }

    localStorage.removeItem("selectedKitchenId");
  };

  const refreshKitchens = async () => {
    try {
      // Refresh current admin info to sync permissions/assignments
      const adminData = await authApi.getCurrentAdmin();
      setAdmin(adminData);

      // Refresh kitchens list and select the active kitchen
      const response = await kitchensApi.getAccessibleKitchens();
      const nextKitchens = response.kitchens || [];
      setKitchens(nextKitchens);

      const storedKitchenId = localStorage.getItem("selectedKitchenId");
      const hasStoredKitchen = storedKitchenId
        ? nextKitchens.some((kitchen) => kitchen._id === storedKitchenId)
        : false;

      if (hasStoredKitchen) {
        setSelectedKitchenIdState(storedKitchenId);
        return;
      }

      if (nextKitchens.length > 0) {
        const firstKitchenId = nextKitchens[0]._id;
        setSelectedKitchenIdState(firstKitchenId);
        localStorage.setItem("selectedKitchenId", firstKitchenId);
        return;
      }

      setSelectedKitchenIdState(null);
      localStorage.removeItem("selectedKitchenId");
    } catch (error) {
      console.error("Error refreshing kitchens in context:", error);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("adminToken");
      if (token) {
        try {
          const adminData = await authApi.getCurrentAdmin();
          setAdmin(adminData);
          await refreshKitchens();
          setIsAuthenticated(true);
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            localStorage.removeItem("adminToken");
            localStorage.removeItem("adminInfo");
            localStorage.removeItem("selectedKitchenId");
          } else if (!axios.isAxiosError(error) || !error.response || error.response.status >= 500) {
            // A network/server failure does not invalidate the saved session.
            // Only restore cached access while the token is still within its 24 hours.
            try {
              const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
              const { exp } = JSON.parse(atob(payload));
              const cachedAdmin = localStorage.getItem("adminInfo");
              if (typeof exp === "number" && exp * 1000 > Date.now() && cachedAdmin) {
                setAdmin(JSON.parse(cachedAdmin));
                setSelectedKitchenIdState(localStorage.getItem("selectedKitchenId"));
                setIsAuthenticated(true);
              }
            } catch {
              // Keep saved credentials for a later successful server check.
            }
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await authApi.login(email, password);
      setAdmin(response.admin);
      await refreshKitchens();
      setIsAuthenticated(true);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminInfo");
      localStorage.removeItem("selectedKitchenId");
      setAdmin(null);
      setKitchens([]);
      setSelectedKitchenIdState(null);
      setIsAuthenticated(false);
    }
  };


  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        admin,
        kitchens,
        selectedKitchenId: selectedKitchenIdState,
        loading,
        login,
        logout,
        setSelectedKitchenId,
        refreshKitchens,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
